/**
 * Shared utility for sorting/grouping CRM users with DTC Loan Officers first.
 *
 * DTC LO status is stored on Firestore user documents via two fields:
 *   - isDTCLoanOfficer (boolean)
 *   - Roles array containing 'DTC Loan Officer'
 *
 * Usage:
 *   import { groupCRMUsersByDTC, sortCRMUsersWithDTCPriority } from '../../utils/crmUserSorting';
 */

/**
 * Check if a user object represents a DTC Loan Officer.
 * Handles both the boolean field and the Roles array.
 * @param {Object} user - A user object from Firestore
 * @returns {boolean}
 */
export function isDTCLoanOfficer(user) {
  return (
    user.isDTCLoanOfficer === true ||
    (Array.isArray(user.Roles) && user.Roles.includes('DTC Loan Officer'))
  );
}

/**
 * Build a name-resolver from the nameField parameter.
 * Accepts a string field name (e.g. 'name') or a function (e.g. u => u.Name || u.name).
 * @param {string|Function} nameField
 * @returns {Function} (user) => string
 */
function buildNameResolver(nameField) {
  return typeof nameField === 'function'
    ? nameField
    : (user) => user[nameField] || '';
}

/**
 * Sort users with DTC LOs first (alphabetically),
 * then remaining users alphabetically.
 * @param {Array} users - Array of user objects
 * @param {string|Function} nameField - Field name to sort by ('name' or 'label'),
 *   or a function (user) => displayName for custom resolution.
 * @returns {Array} New sorted array (does not mutate original)
 */
export function sortCRMUsersWithDTCPriority(users, nameField = 'name') {
  const getName = buildNameResolver(nameField);
  return [...users].sort((a, b) => {
    const aIsDTC = isDTCLoanOfficer(a);
    const bIsDTC = isDTCLoanOfficer(b);
    if (aIsDTC && !bIsDTC) return -1;
    if (!aIsDTC && bIsDTC) return 1;
    return getName(a).localeCompare(getName(b));
  });
}

/**
 * Split users into two groups: DTC LOs and others,
 * each sorted alphabetically by nameField.
 * Useful for rendering <optgroup> / grouped Select options.
 * @param {Array} users - Array of user objects
 * @param {string|Function} nameField - Field name to sort by ('name' or 'label'),
 *   or a function (user) => displayName for custom resolution.
 * @returns {{ dtcUsers: Array, otherUsers: Array }}
 */
export function groupCRMUsersByDTC(users, nameField = 'name') {
  const getName = buildNameResolver(nameField);
  const dtcUsers = [];
  const otherUsers = [];

  for (const user of users) {
    (isDTCLoanOfficer(user) ? dtcUsers : otherUsers).push(user);
  }

  const sorter = (a, b) => getName(a).localeCompare(getName(b));
  dtcUsers.sort(sorter);
  otherUsers.sort(sorter);

  return { dtcUsers, otherUsers };
}
